import { apiFetch } from './api';

/**
 * Admin dashboard data layer for pack assets.
 *
 * Mirrors the camelCase JSON shape returned by `infra/worker/src/routes/admin/assets.ts`'s
 * `serializeAsset(...)` (it transforms the snake_case D1 row before
 * sending it on the wire). All times are ISO-8601 UTC strings.
 *
 * Validates Requirements 22.2, 22.3, 22.4.
 */

export type PackAssetState = 'staged' | 'published' | 'deleted';

export interface PackAsset {
  packId: string;
  assetId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  width: number;
  height: number;
  durationMs: number | null;
  position: number;
  /** R2 object key — not used directly by the SPA but useful for diagnostics. */
  r2Key: string;
  state: PackAssetState;
  uploadedAt: string;
}

interface AssetsListResponse {
  items: PackAsset[];
}

/**
 * Fetch every asset row attached to `packId`, ordered by `position ASC`.
 *
 * Used by the editor to render the thumbnail grid and to compute the
 * "next asset position" when staging a new upload.
 *
 * _Requirements: 22.3_
 */
export async function listAssets(packId: string): Promise<PackAsset[]> {
  const res = await apiFetch<AssetsListResponse>(
    `/admin/v1/packs/${encodeURIComponent(packId)}/assets`,
  );
  return res.items;
}

/**
 * Persist a new display order for the assets in `packId`.
 *
 * The Worker route is `PATCH /admin/v1/packs/{id}/assets/reorder` and
 * expects `{ order: [{assetId, position}, ...] }`. Returns the canonical
 * post-reorder list so the caller can swap its local preview for the
 * server's source of truth in one round-trip.
 *
 * _Requirements: 22.3_
 */
export async function reorderAssets(
  packId: string,
  order: Array<{ assetId: string; position: number }>,
): Promise<PackAsset[]> {
  const res = await apiFetch<AssetsListResponse>(
    `/admin/v1/packs/${encodeURIComponent(packId)}/assets/reorder`,
    {
      method: 'PATCH',
      body: { order },
    },
  );
  return res.items;
}

/**
 * Delete a single asset from a draft pack.
 *
 * The Worker rejects this on non-draft packs with 409 `not_draft`; the
 * editor UI guards against that pre-flight (Requirement 22.4) but we
 * still let the Worker enforce it as the source of truth.
 *
 * _Requirements: 22.4_
 */
export async function deleteAsset(
  packId: string,
  assetId: string,
): Promise<void> {
  await apiFetch<null>(
    `/admin/v1/packs/${encodeURIComponent(packId)}/assets/${encodeURIComponent(
      assetId,
    )}`,
    { method: 'DELETE' },
  );
}

/**
 * Maximum bytes accepted by the Worker upload route (Requirement 18.3).
 * Surfaced here so the dashboard can reject oversized files before
 * spending time hashing them.
 */
export const MAX_ASSET_BYTES = 8 * 1024 * 1024;

/**
 * Maximum assets per pack (Requirement 18.4). Pre-flight check in the
 * uploader so a drag-drop of 50 files into a pack with 180 existing
 * assets surfaces the violation immediately rather than after 20 ok
 * uploads + 30 server rejections.
 */
export const MAX_ASSETS_PER_PACK = 200;

/**
 * Allowed Content-Type values per pack kind. The Worker enforces this
 * server-side too (Requirements 18.1, 18.2, 34.2) but the editor maps
 * it to the `<input type="file" accept="...">` attribute and to a
 * pre-flight check that rejects files dropped through the OS dialog
 * (which ignores the `accept` filter).
 */
export const STICKER_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/webp',
]);
export const GIF_CONTENT_TYPES: ReadonlySet<string> = new Set(['image/gif']);

/**
 * `accept` attribute values for `<input type="file">`. Includes a
 * superset of the strict server-side rules (also accepts `image/jpeg`
 * for sticker drag-drop) so admins can drop a JPEG and see a clear
 * pre-flight rejection rather than a silent OS filter — the task spec
 * explicitly asks for that surface.
 */
export const STICKER_ACCEPT = 'image/png,image/jpeg,image/webp';
export const GIF_ACCEPT = 'image/gif';
